import React, { useEffect, useState } from 'react';
import { Alert, Card, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  bridgeSessionBookingService,
  type BridgeSessionBooking,
} from '../../services/bridgeSessionBookingService';
import { bookingsForCompany } from '../AppShell/companyAdminAccess';
import { companyAdminError } from './companyAdminApi';
import { CompanyAdminGate } from './CompanyAdminGate';

const { Title, Text } = Typography;

const SessionsBody: React.FC<{ companyId: string; companyName: string }> = ({
  companyId,
  companyName,
}) => {
  const [rows, setRows] = useState<BridgeSessionBooking[]>([]);
  const [omitted, setOmitted] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    bridgeSessionBookingService
      .list()
      .then((list) => {
        if (cancelled) return;
        const filtered = bookingsForCompany(list, companyId, companyName);
        setRows(filtered.matched);
        setOmitted(filtered.omitted);
      })
      .catch((err) => {
        if (cancelled) return;
        setRows([]);
        setOmitted(0);
        setError(companyAdminError(err, 'Could not load session bookings.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, companyName]);

  const columns: ColumnsType<BridgeSessionBooking> = [
    {
      title: 'Date & Time',
      dataIndex: 'scheduled_at',
      key: 'scheduled_at',
      render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—'),
    },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (value?: string) => value || companyName,
    },
  ];

  return (
    <Card>
      <Title level={3}>Session schedule</Title>
      <Text type="secondary">Bookings for {companyName}. Other companies on the account are omitted.</Text>
      {error ? <Alert style={{ marginTop: 16 }} type="error" showIcon message={error} /> : null}
      {omitted > 0 ? (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message={`${omitted} booking${omitted === 1 ? '' : 's'} belong to a different company and are hidden.`}
        />
      ) : null}
      <Table
        style={{ marginTop: 16 }}
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: loading ? 'Loading' : 'No sessions for this company.' }}
      />
    </Card>
  );
};

export const CompanyAdminSessionsPage: React.FC = () => (
  <CompanyAdminGate>
    {(target) => <SessionsBody companyId={target.companyId} companyName={target.companyName} />}
  </CompanyAdminGate>
);

export default CompanyAdminSessionsPage;
